import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { customerApi } from "../Apis/Api";
import { apiCall } from "../Apis/apiCall";
import { toast } from "react-toastify";
import Breadcrumb from "../components/Breadcrumb";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import CustomSelect from "../components/common/CustomSelect";
import PlateNumberComponent from "../components/common/PlateNumberComponent";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { X } from "lucide-react";

// Color Options
const colorOptions = [
  { value: "white", label: "White", hex: "#FFFFFF" },
  { value: "black", label: "Black", hex: "#000000" },
  { value: "silver", label: "Silver", hex: "#C0C0C0" },
  { value: "gray", label: "Gray", hex: "#808080" },
  { value: "blue", label: "Blue", hex: "#2196F3" },
  { value: "red", label: "Red", hex: "#F44336" },
  { value: "brown", label: "Brown", hex: "#795548" },
  { value: "green", label: "Green", hex: "#4CAF50" },
  { value: "yellow", label: "Yellow", hex: "#FFEB3B" },
  { value: "gold", label: "Gold", hex: "#FFD700" },
  { value: "beige", label: "Beige", hex: "#F5F5DC" },
  { value: "maroon", label: "Maroon", hex: "#800000" },
  { value: "orange", label: "Orange", hex: "#FF9800" },
  { value: "purple", label: "Purple", hex: "#9C27B0" },
  { value: "navy-blue", label: "Navy Blue", hex: "#000080" },
  { value: "dark-gray", label: "Dark Gray", hex: "#A9A9A9" },
  { value: "light-blue", label: "Light Blue", hex: "#ADD8E6" },
  { value: "teal", label: "Teal", hex: "#009688" },
];

// Color Picker Modal Component
const ColorPickerModal = ({
  isOpen,
  onClose,
  selectedColor,
  onSelectColor,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-base radius-12 shadow-3"
        style={{
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header border-bottom pb-16 px-24 pt-24">
          <div className="d-flex align-items-center justify-content-between w-100">
            <h6 className="text-primary-light m-0">Select Vehicle Color</h6>
            <button
              onClick={onClose}
              className="btn p-0 hover-bg-transparent"
              type="button"
            >
              <X size={20} className="text-primary-light" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body p-24">
          <div className="grid-container" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
            gap: "16px",
          }}>
            {colorOptions.map((color) => {
              const isSelected = selectedColor === color.value;
              
              return (
                <div
                  key={color.value}
                  onClick={() => {
                    onSelectColor(color);
                    onClose();
                  }}
                  className={`color-option radius-8 p-12 cursor-pointer ${
                    isSelected 
                      ? "border border-width-2-px border-primary-600 bg-primary-50" 
                      : "border border-neutral-300 bg-base hover-bg-neutral-50"
                  }`}
                  style={{
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {/* Color Swatch */}
                  <div
                    className="radius-8 mb-8"
                    style={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: color.hex,
                      border: color.value === "white" 
                        ? "1px solid var(--neutral-300)" 
                        : "1px solid " + color.hex,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                  
                  {/* Color Label */}
                  <span className="text-sm text-primary-light text-center">
                    {color.label}
                  </span>
                  
                  {/* Selected Indicator */}
                  {isSelected && (
                    <div className="mt-8">
                      <div className="bg-primary-600 text-base text-xs radius-4 px-8 py-2">
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-top pt-16 px-24 pb-24">
          <div className="d-flex justify-content-end gap-16">
            <button
              type="button"
              onClick={onClose}
              className="btn border border-neutral-300 radius-8 px-20 py-8 text-neutral-700 hover-bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedColor) {
                  onSelectColor({ value: "", label: "", hex: "" });
                }
                onClose();
              }}
              className="btn border border-neutral-300 radius-8 px-20 py-8 text-neutral-700 hover-bg-neutral-100"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Validation Schema
const validationSchema = Yup.object().shape({
  customer_name: Yup.string()
    .required("Customer name is required")
    .min(2, "Name must be at least 2 characters"),
  email: Yup.string()
    .email("Invalid email")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email format"
    ),
  phone_number: Yup.string()
    .required("Phone number is required")
    .test(
      "is-valid-phone",
      "Invalid phone number for selected country",
      (value) => (value ? isValidPhoneNumber(value) : false)
    ),
  national_id: Yup.string().optional(),
  notes: Yup.string().optional(),
  plate_number: Yup.string().required("Plate number is required"),
  vehicle_type: Yup.string().required("Vehicle type is required"),
  vehicle_make: Yup.string().required("Vehicle make is required"),
  vehicle_model: Yup.string().required("Vehicle model is required"),
  vehicle_color: Yup.string().required("Vehicle color is required"),
  countryId: Yup.string().optional(),
  authorityId: Yup.string().optional(),
});

const AddCustomerProfilePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    types: false,
    makes: false,
    models: false,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      customer_name: "",
      email: "",
      phone_number: "",
      national_id: "",
      notes: "",
      plate_number: "",
      vehicle_type: "",
      vehicle_make: "",
      vehicle_model: "",
      vehicle_color: "",
      countryId: "",
      authorityId: "",
    },
  });

  const selectedColorValue = watch("vehicle_color");
  const selectedColorObj = colorOptions.find(
    (c) => c.value === selectedColorValue
  );

  // Fetch Vehicle Types
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, types: true }));
        const response = await apiCall(
          "get",
          "/get-all-vehicle-types",
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        if (response?.data?.success && Array.isArray(response.data.data)) {
          setVehicleTypes(response.data.data);
        } else {
          setVehicleTypes([]);
          console.warn("Vehicle types data is not an array:", response?.data);
        }
      } catch (error) {
        console.error("Error fetching vehicle types:", error);
        setVehicleTypes([]);
        toast.error("Failed to load vehicle types");
      } finally {
        setLoadingStates((prev) => ({ ...prev, types: false }));
      }
    };
    fetchVehicleTypes();
  }, [navigate]);

  // Fetch Vehicle Makes
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, makes: true }));
        const response = await apiCall(
          "get",
          "/get-all-vehicle-makes",
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        console.log("Vehicle Makes Response:", response);
        const makesData = response?.data?.data?.data || response?.data?.data;
        if (response?.data?.success && Array.isArray(makesData)) {
          setVehicleMakes(makesData);
        } else {
          setVehicleMakes([]);
          console.warn("Vehicle makes data is not an array:", response?.data);
        }
      } catch (error) {
        console.error("Error fetching vehicle makes:", error);
        setVehicleMakes([]);
        toast.error("Failed to load vehicle makes");
      } finally {
        setLoadingStates((prev) => ({ ...prev, makes: false }));
      }
    };
    fetchVehicleMakes();
  }, [navigate]);

  // Fetch Vehicle Models when Make is selected
  useEffect(() => {
    const fetchVehicleModels = async () => {
      if (!selectedMake) {
        setVehicleModels([]);
        return;
      }
      try {
        setLoadingStates((prev) => ({ ...prev, models: true }));
        const response = await apiCall(
          "get",
          `/get-vehicle-models-by-make/${selectedMake}`,
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        console.log("Vehicle Models Response:", response);
        const modelsData = response?.data?.data?.data || response?.data?.data;
        if (response?.data?.success && Array.isArray(modelsData)) {
          setVehicleModels(modelsData);
        } else {
          setVehicleModels([]);
          console.warn("Vehicle models data is not an array:", response?.data);
        }
      } catch (error) {
        console.error("Error fetching vehicle models:", error);
        setVehicleModels([]);
        toast.error("Failed to load vehicle models");
      } finally {
        setLoadingStates((prev) => ({ ...prev, models: false }));
      }
    };
    fetchVehicleModels();
  }, [selectedMake, navigate]);

  const onSubmit = async (data) => {
    console.log("Form Data:", data);
    try {
      setIsLoading(true);
      const payload = {
        customer_name: data.customer_name,
        email: data.email || undefined,
        mobile_number: data.phone_number,
        national_id: data.national_id || undefined,
        notes: data.notes || undefined,
        plate_number: data.plate_number,
        vehicle_type: parseInt(data.vehicle_type),
        vehicle_make: parseInt(data.vehicle_make),
        vehicle_model: parseInt(data.vehicle_model),
        vehicle_color: data.vehicle_color,
        registration_country_id: data.countryId
          ? parseInt(data.countryId)
          : undefined,
        plate_authority_id: data.authorityId
          ? parseInt(data.authorityId)
          : undefined,
      };
      console.log("Payload:", payload);
      const response = await customerApi.addCustomerProfile(payload);

      const isSuccess =
        response?.success === true ||
        response?.status === "success" ||
        response?.code === 200 ||
        response?.statusCode === 200 ||
        !response?.error;

      if (isSuccess) {
        toast.success("Customer profile created successfully!");
        reset();
        navigate("/customer-profiles");
      } else {
        toast.error(
          response?.message ||
            response?.error ||
            "Failed to create customer profile"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to create customer profile");
    } finally {
      setIsLoading(false);
    }
  };

  // Transform options for CustomSelect
  const vehicleTypeOptions = Array.isArray(vehicleTypes)
    ? vehicleTypes.map((type) => ({
        value: type.type_id,
        label: type.type_name,
      }))
    : [];

  const vehicleMakeOptions = Array.isArray(vehicleMakes)
    ? vehicleMakes.map((make) => ({
        value: make.make_id,
        label: make.make_name,
      }))
    : [];

  const vehicleModelOptions = Array.isArray(vehicleModels)
    ? vehicleModels.map((model) => ({
        value: model.model_id,
        label: model.model_name,
      }))
    : [];

  useEffect(() => {
    console.log("Form Errors:", errors);
  }, [errors]);

  return (
    <>
      <Breadcrumb title="Add Customer" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-20">
              {/* Customer Name */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Customer Name *</label>
                <input
                  type="text"
                  {...register("customer_name")}
                  className={`form-control radius-8 ${
                    errors.customer_name ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Customer Name"
                />
                {errors.customer_name && (
                  <div className="text-danger">
                    {errors.customer_name.message}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  {...register("email")}
                  className={`form-control radius-8 ${
                    errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Email (Optional)"
                />
                {errors.email && (
                  <div className="text-danger">{errors.email.message}</div>
                )}
              </div>

              {/* Phone Number */}
              <div className="col-md-6 mb-20">
                <CustomPhoneInput
                  name="phone_number"
                  control={control}
                  errors={errors}
                />
              </div>

              {/* National ID */}
              <div className="col-md-6 mb-20">
                <label className="form-label">National ID</label>
                <input
                  type="text"
                  {...register("national_id")}
                  className={`form-control radius-8 ${
                    errors.national_id ? "is-invalid" : ""
                  }`}
                  placeholder="Enter National ID (Optional)"
                />
                {errors.national_id && (
                  <div className="text-danger">
                    {errors.national_id.message}
                  </div>
                )}
              </div>

              {/* Plate Number Component */}
              <div className="col-md-12 mb-20">
                <label className="form-label">Plate Number *</label>
                <Controller
                  name="plate_number"
                  control={control}
                  render={({ field }) => (
                    <PlateNumberComponent
                      field={{ ...field, name: "plate_number", required: true }}
                      formData={watch()}
                      setFormData={(updater) => {
                        const newData =
                          typeof updater === "function"
                            ? updater(watch())
                            : updater;
                        Object.keys(newData).forEach((key) => {
                          setValue(key, newData[key]);
                        });
                      }}
                    />
                  )}
                />
                {errors.plate_number && (
                  <div className="text-danger">
                    {errors.plate_number.message}
                  </div>
                )}
              </div>

              {/* Vehicle Type */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Vehicle Type *</label>
                <Controller
                  name="vehicle_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleTypeOptions}
                      placeholder="Select Vehicle Type"
                      isSearchable={true}
                      isClearable={true}
                      isLoading={loadingStates.types}
                      value={
                        vehicleTypeOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                    />
                  )}
                />
                {errors.vehicle_type && (
                  <div className="text-danger">
                    {errors.vehicle_type.message}
                  </div>
                )}
              </div>

              {/* Vehicle Make */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Vehicle Make *</label>
                <Controller
                  name="vehicle_make"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleMakeOptions}
                      placeholder="Select Vehicle Make"
                      isSearchable={true}
                      isClearable={true}
                      isLoading={loadingStates.makes}
                      value={
                        vehicleMakeOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) => {
                        field.onChange(selected ? selected.value : "");
                        setSelectedMake(selected ? selected.value : null);
                        setValue("vehicle_model", "");
                      }}
                    />
                  )}
                />
                {errors.vehicle_make && (
                  <div className="text-danger">
                    {errors.vehicle_make.message}
                  </div>
                )}
              </div>

              {/* Vehicle Model */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Vehicle Model *</label>
                <Controller
                  name="vehicle_model"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleModelOptions}
                      placeholder={
                        selectedMake
                          ? "Select Vehicle Model"
                          : "Select Make First"
                      }
                      isSearchable={true}
                      isClearable={true}
                      isDisabled={!selectedMake}
                      isLoading={loadingStates.models}
                      value={
                        vehicleModelOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                    />
                  )}
                />
                {errors.vehicle_model && (
                  <div className="text-danger">
                    {errors.vehicle_model.message}
                  </div>
                )}
              </div>

              {/* Vehicle Color - Updated to match style.css */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Vehicle Color *</label>
                <div
                  onClick={() => setIsColorModalOpen(true)}
                  className={`radius-8 border ${
                    errors.vehicle_color 
                      ? "border-danger-500" 
                      : "border-neutral-300"
                  } cursor-pointer hover-bg-neutral-50 transition-2`}
                  style={{
                    padding: "11px 16px",
                    minHeight: "46px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "var(--white)",
                  }}
                >
                  {selectedColorObj ? (
                    <div className="d-flex align-items-center gap-10">
                      <div
                        className="radius-4"
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: selectedColorObj.hex,
                          border: selectedColorObj.value === "white" 
                            ? "1px solid var(--neutral-300)" 
                            : "1px solid " + selectedColorObj.hex,
                        }}
                      />
                      <span className="text-primary-light">{selectedColorObj.label}</span>
                    </div>
                  ) : (
                    <span className="text-neutral-500">Select Vehicle Color</span>
                  )}
                  <div className="ms-auto">
                    <div className="text-xs text-neutral-400">Click to select</div>
                  </div>
                </div>
                {errors.vehicle_color && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.vehicle_color.message}
                  </div>
                )}
              </div>

              {/* Color Picker Modal */}
              <ColorPickerModal
                isOpen={isColorModalOpen}
                onClose={() => setIsColorModalOpen(false)}
                selectedColor={selectedColorValue}
                onSelectColor={(color) =>
                  setValue("vehicle_color", color.value)
                }
              />

              {/* Notes */}
              <div className="col-md-12 mb-20">
                <label className="form-label">Notes</label>
                <textarea
                  {...register("notes")}
                  className="form-control radius-8"
                  rows="4"
                  placeholder="Enter notes (optional)"
                />
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={isLoading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddCustomerProfilePage;